import { Row } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Timeline from 'components/new-ui/base/timeline/Timeline';
import { H2 } from 'components/new-ui/base/typography/styles';
import HistoryItem from 'components/new-ui/composed/history/HistoryItem';
import { GetManifestHistoryParams, ManifestHistory as History } from 'store/manifests/types';
import { getHistoryByManifestId } from 'store/manifests/client';
import Notification from 'components/ui/base/notification/Notification';
import { getManifestHistoryTypeLabel } from '../utils';
import Button from 'components/new-ui/base/button/Button';
import { getErrorMessage } from 'utils/getErrorMessage';
import RangePicker from 'components/new-ui/base/date-picker/RangePicker';

type DateRange = {
    startDate: moment.Moment;
    endDate: moment.Moment;
};

const PAGE_SIZE = 20;

const ManifestHistory = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null);
    const { manifestId } = useParams<{ manifestId: string }>();
    const [history, setHistory] = useState<History[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    const controllerRef = useRef<AbortController | null>(null);

    const handleDateRangeChange = (data: [moment.Moment | null, moment.Moment | null] | null) => {
        const [startDate, endDate] = data || [];

        if (!startDate || !endDate) {
            setSelectedDateRange(null);
            searchParams.delete('startDate');
            searchParams.delete('endDate');
            setSearchParams(searchParams);

            setHistory([]);
            fetchPage(0);
            return;
        }

        // Normalize the timestamps
        const startOfDay = startDate.clone().startOf('day');
        const endOfDay = endDate.clone().endOf('day');
        setSelectedDateRange({ startDate: startOfDay, endDate: endOfDay });

        searchParams.set('startDate', startOfDay.toISOString());
        searchParams.set('endDate', endOfDay.toISOString());
        setSearchParams(searchParams);

        setHistory([]);
        fetchPage(0);
    };

    const fetchPage = useCallback(
        async (pageToLoad: number) => {
            if (!manifestId) return;

            controllerRef.current?.abort();
            const controller = new AbortController();
            controllerRef.current = controller;

            setIsLoading(true);

            try {
                const params: GetManifestHistoryParams = { page: pageToLoad, size: PAGE_SIZE };

                if (selectedDateRange?.startDate && selectedDateRange?.endDate) {
                    params.startDate = selectedDateRange.startDate.clone().startOf('day').toISOString();
                    params.endDate = selectedDateRange.endDate.clone().endOf('day').toISOString();
                }

                const { list, pageNumber, pageSize, total } = await getHistoryByManifestId(manifestId, params);

                if (controller.signal.aborted) return;

                // Deduplicate in case of overlaps
                setHistory((prev) => {
                    const seen = new Set(prev.map((i) => i.id));
                    const merged = [...prev];

                    for (const item of list) {
                        if (!seen.has(item.id)) merged.push(item);
                    }
                    return merged;
                });

                const effectiveSize = pageSize || PAGE_SIZE;
                const totalPages = Math.ceil((total ?? 0) / effectiveSize);
                setHasMore(pageNumber + 1 < totalPages && list.length > 0);
                setPage(pageNumber);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Error',
                    description: errorMessage || 'Could not get history for this manifest',
                });
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        },
        [manifestId, selectedDateRange?.endDate, selectedDateRange?.startDate]
    );

    useEffect(() => {
        setHistory([]);
        setPage(0);
        setHasMore(false);

        if (manifestId) {
            fetchPage(0);
        }

        return () => {
            controllerRef.current?.abort();
        };
    }, [manifestId, fetchPage]);

    useEffect(() => {
        const start = searchParams.get('startDate');
        const end = searchParams.get('endDate');

        if (start && end) {
            const startDate = moment(start).startOf('day');
            const endDate = moment(end).endOf('day');
            setSelectedDateRange({ startDate, endDate });
        } else {
            setSelectedDateRange(null);
        }
    }, [searchParams]);

    return (
        <div>
            <Row
                justify="space-between"
                align="middle"
                style={{
                    marginBottom: 32,
                }}
            >
                <H2>Changelog</H2>
                <div>
                    <RangePicker
                        value={selectedDateRange ? [selectedDateRange.startDate, selectedDateRange.endDate] : undefined}
                        onChange={handleDateRangeChange}
                    />
                </div>
            </Row>

            <Timeline>
                {history.map(({ id, type, createdAt, creator }) => {
                    return (
                        <Timeline.Item key={id}>
                            <HistoryItem
                                title={getManifestHistoryTypeLabel(type)}
                                createdAt={createdAt}
                                createdBy={creator}
                            />
                        </Timeline.Item>
                    );
                })}
            </Timeline>

            {hasMore && history.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                    <Button loading={isLoading} onClick={() => fetchPage(page + 1)}>
                        Show more
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ManifestHistory;
